<template>
  <div>
    <div v-if="isSuccess">
      <div v-if="hasData">
        <div class="box mt-0 px-0">
          <div :class="{ 'wrapper--adjust': categoriesVisible }">
            <div class="box__pos-header">
              <Values :data="total" />

              <v-spacer />

              <FeaturedCategory
                v-show="featuredCategoryVisible"
                :data="rideCategoryGroup.mostUsed"
              />

              <CategoriesAction
                :isClosed="categoriesVisible"
                :percentage="rideCategoryGroup.lessUsedSum"
                @toggleVisibility="handleToggleCategoriesVisibility"
              />
            </div>

            <Categories v-show="categoriesVisible" :categories="rideCategoryOrdened" />
          </div>
        </div>

        <div class="box box__chart mt-4 pt-2">
          <h2 class="chart__title">{{ $t(`${namespace}.title`) }}</h2>
          <Chart :data="chartData" />
        </div>
      </div>

      <EmptyState
        v-else
        :categoriesVisible="categoriesVisible"
        :minHeight="422"
      />
    </div>

    <ErrorState
      v-if="isFailed"
      :categoriesVisible="categoriesVisible"
      :minHeight="422"
      @loadData="loadData"
    />

    <LoadingState
      v-if="isLoading"
      :categoriesVisible="categoriesVisible"
      :minHeight="422"
    />
  </div>
</template>

<script>
import { isObject, isArray, objectToArray } from 'utils/functions'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { mapState } from 'vuex'
import Values from './Values'
import FeaturedCategory from './FeaturedCategory'
import CategoriesAction from './CategoriesAction'
import Categories from './Categories'
import Chart from './Chart'
import EmptyState from './EmptyState'
import ErrorState from './ErrorState'
import LoadingState from './LoadingState'

export default {
  name: 'Performance',
  props: {
    filters: {
      type: Object,
      default: () => {}
    }
  },
  components: {
    Values,
    FeaturedCategory,
    CategoriesAction,
    Categories,
    Chart,
    EmptyState,
    ErrorState,
    LoadingState
  },
  computed: {
    ...mapState({
      company: ({ company }) => company.selectedCompany
    }),

    isLoading () {
      return this.currentStatus === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.currentStatus === STATUS_SUCCESS
    },

    isFailed () {
      return this.currentStatus === STATUS_FAILED
    },

    hasData () {
      return isObject(this.total) &&
        (isObject(this.rideCategoryGroup) && this.rideCategoryGroup.mostUsed && this.rideCategoryGroup.mostUsed.percentage > 0) &&
        isArray(this.rideCategoryOrdened) &&
        isObject(this.chartData)
    },

    rideCategoryOrdened () {
      /* todo: ridesCategoriesList */
      return [
        ...objectToArray(this.$store.state.commonConfig.categories),
        ...objectToArray(this.$store.state.commonConfig.b2cCategories)
      ].map(cat => {
        const name = cat.value
        const categoriesPercent = isArray(this.categoriesPercent)
          ? this.categoriesPercent
          : []
        const categoryTarget = categoriesPercent.find(c => c.category === name)
        const percentage = categoryTarget ? categoryTarget.categoryPercentile : 0
        return { ...cat, percentage }
      })
        .sort((a, b) => a.percentage < b.percentage ? 1 : -1)
    },

    rideCategoryGroup () {
      const [head = [], ...tail] = this.rideCategoryOrdened || []
      const mostUsed = this.isValidObject(head) ? { ...head } : {}
      const lessUsedSum = tail.reduce((acc, item) => {
        acc += item.percentage
        return acc
      }, 0)
      return { mostUsed, lessUsedSum }
    },

    featuredCategoryVisible () {
      return !this.categoriesVisible &&
        this.isValidObject(this.rideCategoryGroup.mostUsed) &&
        this.rideCategoryGroup.mostUsed.percentage > 0
    }
  },
  data () {
    return {
      namespace: 'Components.Performance',
      categoriesVisible: false,
      currentStatus: STATUS_IS_LOADING,
      total: {},
      categoriesPercent: [],
      chartData: {},
      preventReload: false
    }
  },
  methods: {
    isValidObject (val) {
      return isObject(val)
    },

    isValidArray (val) {
      return isArray(val)
    },

    handleToggleCategoriesVisibility () {
      this.categoriesVisible = !this.categoriesVisible
    },

    formatDate (str) {
      const arr = str.split('-')
      return `${arr[2]}/${arr[1]}/${arr[0]}`
    },

    async loadData () {
      if (this.preventReload) return

      this.preventReload = true
      this.currentStatus = STATUS_IS_LOADING
      this.categoriesVisible = false

      try {
        const { startDate = null, endDate = null } = this.filters || {}
        const companyId = this.company ? this.company.id : null
        const { data: res } = await this.$apiService.getChart(
          { startDate },
          { endDate },
          { companyId }
        )

        if (
          (this.isValidObject(res.data) && res.data.total) &&
          (this.isValidObject(res.data) && res.data.categories) &&
          (this.isValidObject(res.data) && this.isValidArray(res.data.series))
        ) {
          // values
          this.total = { ...res.data.total }

          // categories
          this.categoriesPercent = [ ...res.data.categories ]

          // chart data
          if (this.isValidObject(res.data) && this.isValidArray(res.data.series)) {
            const { series } = res.data
            this.chartData.amount = series.map(item => item.amount)
            this.chartData.averageFare = series.map(item => item.averageFare)
            // this.chartData.date = series.map(item => this.formatDate(item.date))
            this.chartData.date = series.map(item => item.date)
            this.chartData.totalFare = series.map(item => item.totalFare)
          }
        }

        this.currentStatus = STATUS_SUCCESS
      } catch (err) {
        console.log('ERROR', err)
        this.currentStatus = STATUS_FAILED
      }

      setTimeout(() => { this.preventReload = false }, 1000)
    }
  },
  watch: {
    filters: {
      deep: true,
      handler () {
        this.loadData()
      }
    },
    company () {
      this.loadData()
    }
  },
  mounted () {
    this.loadData()
  }
}
</script>

<style lang="scss" scoped>
.chart__title {
  left: 20px;
  position: absolute;
  top: 12px;
  z-index: 1;
}

.box__chart {
  padding: 4px 16px 10px;
}

.box {
  background-color: #fff;
  border: 1px solid $gray-2;
  border-radius: 4px;
  min-height: 84px;
  margin: 12px;
  position: relative;
  transition: box-shadow .26s;

  &:hover {
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.2);
  }
}

.box__header,
.box__body {
  padding: 20px;
}

.box__header {
  border-radius: 4px 4px 0 0;
  color: $gray-7;
  font-family: 'Montserrat', sans-serif;
  font-size: 16px;
  font-weight: 800;
  line-height: normal;
  padding-bottom: 0;

  @media (min-width: 768px) {
    font-size: 18px;
  }
}

.box__pos-header {
  align-items: center;
  padding: 20px;

  .values {
    margin-bottom: 20px;
  }

  .featured-category {
    display: inline-flex;
  }

  .toogle-categories {
    display: inline-flex;
    margin-top: 8px;

    @media (min-width: 380px) {
      margin-top: 0;
    }
  }

  @media (min-width: 768px) {
    align-items: center;
    display: flex;

    .values {
      margin-bottom: 0
    }

    .featured-category {

      .label-category {
        margin-top: 0;
      }
    }
  }
}

.wrapper {
  min-height: 84px;

  &.wrapper--adjust {
    min-height: 97px;
  }
}
</style>
